import styled from 'styled-components';
import { format, isSameDay } from 'date-fns';
import { body2, borderRadius, colors, spacing, subtitle2 } from 'ui/theme';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { PopoverEvent } from './PopoverEvents';
import { CampaignEvent } from 'modules/campaignsCalendar/hooks';
import { parseDate } from 'utils/datetime';
import { pxToRem } from 'utils/convert';

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: 1fr;
  grid-gap: ${spacing.single};
`;

const WeekDayColumn = styled.div`
  border: ${spacing.single} solid ${colors.grey15};
  padding: ${spacing.half};

  &[data-today="true"] {
    background-color: ${colors.backgroundInteractive};
  }
`;

const WeekDayHeaderContainer = styled.div`
  padding: ${spacing.quarter};
  background-color: ${colors.white};
  text-align: center;

  &[data-today="true"] {
    background-color: ${colors.backgroundInteractive};
  }
`;

const WeekDayHeader = styled.div`
  ${subtitle2};
  padding-bottom: ${spacing.threeQuarter};
  border-bottom:${spacing.single} solid ${colors.grey15};
`;

const WeekDayDate = styled.div`
  color: ${colors.grey15};
  margin-bottom: ${spacing.half};
`;

const WeekDayEventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.half};
  overflow-y: auto;
  height: ${pxToRem(500)};
  `;

const EventItem = styled.div`
  ${body2};
  background-color: ${colors.purple20};
  border-radius: ${borderRadius.md};
  border: none;
  padding: ${spacing.half};
  width: 100%;
  cursor: pointer;

  &:hover {
      background-color: ${colors.purple100};
      color: ${colors.white};
    }
`;

const StyledPopoverContent = styled(PopoverContent)`
  padding: ${spacing.half};
`;

interface WeeklyViewProps {
  days: Date[];
  events?: CampaignEvent[];
}

export const WeeklyView = ({ days, events = [] }: WeeklyViewProps) => {
  const getEventsForDay = (day: Date) => {
    return events?.filter((event) => {
      const eventDate = parseDate(event.start);
      return isSameDay(eventDate, day);
    });
  };

  return (
    <WeekGrid data-test="weekly-view">
      {days.map((day) => {
        const isToday = isSameDay(day, parseDate());
        const dayEvents = getEventsForDay(day);
        return (
          <WeekDayColumn
            data-test={`week-day-${format(day, 'yyyy-MM-dd')}`}
            key={format(day, 'yyyy-MM-dd')}
            data-today={isToday}
          >
            <WeekDayHeaderContainer data-today={isToday}>
              <WeekDayHeader>{format(day, 'EEEE')}</WeekDayHeader>
              <WeekDayDate>{format(day, 'MMM d')}</WeekDayDate>
            </WeekDayHeaderContainer>
            <WeekDayEventsContainer>
              {dayEvents?.map((event) => (
                <Popover key={event.id}>
                  <StyledPopoverContent>
                    <p>{format(day, 'MMMM d, yyyy')}</p>
                    <PopoverEvent event={event} />
                  </StyledPopoverContent>
                  <PopoverTrigger>
                    <EventItem>
                      {format(parseDate(event.start), 'h:mm a')} - {event.title}
                    </EventItem>
                  </PopoverTrigger>
                </Popover>
              ))}
            </WeekDayEventsContainer>
          </WeekDayColumn>
        );
      })}
    </WeekGrid>
  );
};
